import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState, type ReactNode } from 'react';
import { View } from 'react-native';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';

type CollapseSectionProps = {
  title: string;
  /** Estado à direita do título, em repouso (ex.: "opcional — nenhum"). */
  status?: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
};

/**
 * Bloco que abre/fecha animando a altura medida nos dois sentidos. Cabeçalho
 * estático fora da animação. Padrão de `collapse-reveal`.
 */
export function CollapseSection({
  title,
  status,
  expanded,
  onToggle,
  children,
}: CollapseSectionProps) {
  const [contentHeight, setContentHeight] = useState(0);
  const progress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    progress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [expanded, progress]);

  const collapseStyle = useAnimatedStyle(() => ({
    height: progress.value * contentHeight,
    opacity: progress.value,
  }));

  return (
    <View
      className="rounded-2xl border border-neutral-900/5 bg-card"
      style={{ borderCurve: 'continuous' }}
    >
      <PressableScale
        haptic
        onPress={onToggle}
        className="flex-row items-center gap-3 p-4"
        accessibilityRole="button"
        accessibilityState={{ expanded }}
      >
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {title}
        </ThemedText>
        {status ? (
          <ThemedText className="ml-auto text-xs text-neutral-400">{status}</ThemedText>
        ) : null}
        <Ionicons className="text-neutral-400"
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          style={status ? undefined : { marginLeft: 'auto' }}
        />
      </PressableScale>

      <AnimatedView style={[{ overflow: 'hidden' }, collapseStyle]}>
        <View
          style={{ position: 'absolute', left: 0, right: 0 }}
          onLayout={(e) => {
            const h = e.nativeEvent.layout.height;
            if (h > 0 && Math.abs(h - contentHeight) > 0.5) setContentHeight(h);
          }}
        >
          <View className="border-t border-neutral-100 p-4">{children}</View>
        </View>
      </AnimatedView>
    </View>
  );
}
